/**
 * PlaylistDetail Component
 * Displays a playlist and its movies, with status updates via polling
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  deletePlaylist,
  getPlaylist,
  getWatchedMovies,
  markMovieWatched,
  pollPlaylistStatus,
  removeMovieFromPlaylist,
  unmarkMovieWatched,
  type GetPlaylistParams,
  type PaginationInfo,
  type Playlist,
  type PlaylistFilterOptions,
  type PlaylistMovie,
  type SharedByInfo,
} from '../api';
import AddMovieModal from './AddMovieModal';
import EditPlaylistModal from './EditPlaylistModal';
import PlaylistStatusBadge from './PlaylistStatusBadge';
import RatingBadges from './RatingBadges';
import SharePlaylistModal from './SharePlaylistModal';

interface PlaylistProgress {
  status: string;
  found: number;
  approved: number;
  message?: string;
}

// Truncated text component with expand/collapse
function TruncatedText({ text, maxLines = 2 }: { text: string; maxLines?: number }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <p
      className={`movie-overview ${expanded ? 'expanded' : ''}`}
      style={{ WebkitLineClamp: expanded ? 'unset' : maxLines }}
      onClick={() => setExpanded(!expanded)}
    >
      {text}
    </p>
  );
}

// Overflow menu component
function OverflowMenu({
  onEdit,
  onDelete,
  deleting,
}: {
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="overflow-menu">
      <button
        className="overflow-menu-trigger"
        onClick={() => setOpen(!open)}
        aria-label="More options"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open && (
        <>
          <div className="overflow-menu-backdrop" onClick={() => setOpen(false)} />
          <div className="overflow-menu-dropdown">
            <button
              className="overflow-menu-item"
              onClick={() => {
                onEdit();
                setOpen(false);
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Edit Playlist
            </button>
            <button
              className="overflow-menu-item danger"
              onClick={() => {
                onDelete();
                setOpen(false);
              }}
              disabled={deleting}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14" />
              </svg>
              {deleting ? 'Deleting...' : 'Delete Playlist'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function PlaylistDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial state from URL params
  const getInitialPage = () => {
    const p = searchParams.get('page');
    return p ? parseInt(p, 10) : 1;
  };
  const getInitialSortBy = (): 'year' | 'title' | 'watched_at' | 'added_at' => {
    const s = searchParams.get('sortBy');
    if (s === 'title' || s === 'year' || s === 'watched_at' || s === 'added_at') return s;
    // Defaults are set after playlist loads (see useEffect below)
    return 'year';
  };
  const getInitialSortOrder = (): 'asc' | 'desc' => {
    const s = searchParams.get('sortOrder');
    if (s === 'asc' || s === 'desc') return s;
    return 'asc';
  };
  const getInitialLanguage = () => searchParams.get('language') || '';
  const getInitialYearMin = () => {
    const y = searchParams.get('yearMin');
    return y ? parseInt(y, 10) : undefined;
  };
  const getInitialYearMax = () => {
    const y = searchParams.get('yearMax');
    return y ? parseInt(y, 10) : undefined;
  };
  const getInitialGenre = () => searchParams.get('genre') || '';

  const [playlist, setPlaylist] = useState<Playlist | null>(null);
  const [movies, setMovies] = useState<PlaylistMovie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<PlaylistProgress | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'year' | 'title' | 'watched_at' | 'added_at'>(getInitialSortBy);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(getInitialSortOrder);
  const [sortDefaultsApplied, setSortDefaultsApplied] = useState(false);
  const [removingMovieId, setRemovingMovieId] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [permission, setPermission] = useState<'owner' | 'read' | 'edit'>('owner');
  const [sharedBy, setSharedBy] = useState<SharedByInfo | null>(null);

  // Pagination state - initialize from URL
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: getInitialPage(),
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Filter state - initialize from URL
  const [languageFilter, setLanguageFilter] = useState<string>(getInitialLanguage);
  const [yearMin, setYearMin] = useState<number | undefined>(getInitialYearMin);
  const [yearMax, setYearMax] = useState<number | undefined>(getInitialYearMax);
  const [genreFilter, setGenreFilter] = useState<string>(getInitialGenre);
  const [filterOptions, setFilterOptions] = useState<PlaylistFilterOptions>({
    languages: [],
    yearRange: null,
    genres: [],
  });

  // Update URL when filter state changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (pagination.page > 1) params.set('page', pagination.page.toString());
    if (sortBy !== 'year' || sortOrder !== 'asc') {
      params.set('sortBy', sortBy);
      params.set('sortOrder', sortOrder);
    }
    if (languageFilter) params.set('language', languageFilter);
    if (yearMin !== undefined) params.set('yearMin', yearMin.toString());
    if (yearMax !== undefined) params.set('yearMax', yearMax.toString());
    if (genreFilter) params.set('genre', genreFilter);

    // Use replace to avoid adding to browser history on every filter change
    setSearchParams(params, { replace: true });
  }, [
    pagination.page,
    sortBy,
    sortOrder,
    languageFilter,
    yearMin,
    yearMax,
    genreFilter,
    setSearchParams,
  ]);

  const playlistId = id ? parseInt(id, 10) : null;
  const isOwner = permission === 'owner';
  const canEdit = permission === 'owner' || permission === 'edit';
  const isSystemPlaylist = playlist?.type != null;
  const isWantToWatch = playlist?.type === 'want_to_watch';
  const isWatchedPlaylist = playlist?.type === 'watched';

  // Watched movies state
  const [watchedMovies, setWatchedMovies] = useState<Set<number>>(new Set());
  const [hideWatched, setHideWatched] = useState<boolean>(() => {
    // Load preference from localStorage
    if (playlistId) {
      const saved = localStorage.getItem(`playlist-${playlistId}-hide-watched`);
      return saved === 'true';
    }
    return false;
  });
  const [togglingWatched, setTogglingWatched] = useState<number | null>(null);

  // Watched stats from API (totals across ALL movies in playlist)
  const [totalWatchedCount, setTotalWatchedCount] = useState(0);
  const [totalMovieCount, setTotalMovieCount] = useState(0);
  const [friendsWatchedCount, setFriendsWatchedCount] = useState(0);
  const [friendsWatchedMovies, setFriendsWatchedMovies] = useState<Set<number>>(new Set());

  // Calculate watched percentage using totals from API (across ALL movies)
  const watchedPercentage =
    totalMovieCount > 0 ? Math.round((totalWatchedCount / totalMovieCount) * 100) : 0;

  // Client-side filtering for search query and hide watched (sorting is done server-side)
  const filteredMovies = movies.filter((movie) => {
    // Filter by search query (client-side for instant feedback)
    if (searchQuery.trim() !== '') {
      if (!movie.title?.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
    }
    // Filter by watched status (client-side toggle)
    if (hideWatched && watchedMovies.has(movie.tmdb_id)) {
      return false;
    }
    return true;
  });

  const loadPlaylist = useCallback(
    async (page: number = pagination.page) => {
      if (!playlistId) return;

      const params: GetPlaylistParams = {
        page,
        limit: 20,
        sortBy,
        sortOrder,
        language: languageFilter || undefined,
        yearMin,
        yearMax,
        genre: genreFilter || undefined,
      };

      try {
        const [playlistData, watchedData] = await Promise.all([
          getPlaylist(playlistId, params),
          getWatchedMovies(),
        ]);
        setPlaylist(playlistData.playlist);
        setMovies(playlistData.movies);
        setPagination(playlistData.pagination);
        setFilterOptions(playlistData.filterOptions);
        setWatchedMovies(new Set(watchedData));
        setPermission(playlistData.permission);
        setSharedBy(playlistData.shared_by);
        setTotalWatchedCount(playlistData.watched_count);
        setTotalMovieCount(playlistData.total_count);
        setFriendsWatchedCount(playlistData.friends_watched_count);
        setFriendsWatchedMovies(new Set(playlistData.friends_watched_movie_ids));
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load playlist');
      } finally {
        setLoading(false);
      }
    },
    [playlistId, sortBy, sortOrder, languageFilter, yearMin, yearMax, genreFilter, pagination.page]
  );

  // Initial load - use page from URL params
  useEffect(() => {
    loadPlaylist(pagination.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistId]);

  // Apply default sort for system playlists on first load
  useEffect(() => {
    if (playlist && !sortDefaultsApplied) {
      setSortDefaultsApplied(true);
      const hasSortParam = searchParams.has('sortBy');
      const hasOrderParam = searchParams.has('sortOrder');
      if (!hasSortParam && playlist.type === 'watched') {
        setSortBy('watched_at');
        if (!hasOrderParam) setSortOrder('desc');
      } else if (!hasSortParam && playlist.type === 'want_to_watch') {
        setSortBy('added_at');
        if (!hasOrderParam) setSortOrder('desc');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlist]);

  // Refetch when sort/filter changes (reset to page 1)
  useEffect(() => {
    if (playlist) {
      // Only refetch if we have loaded the playlist initially
      loadPlaylist(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder, languageFilter, yearMin, yearMax, genreFilter]);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      loadPlaylist(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Poll for status updates while playlist is processing
  const playlistStatus = playlist?.status;
  useEffect(() => {
    if (!playlistId || !playlistStatus) return;
    if (playlistStatus !== 'pending' && playlistStatus !== 'processing') return;

    const controller = pollPlaylistStatus(playlistId, (data) => {
      setProgress(data);

      // Update playlist status
      if (data.status === 'completed' || data.status === 'failed') {
        setPlaylist((prev) =>
          prev
            ? {
                ...prev,
                status: data.status as Playlist['status'],
                progress_found: data.found,
                progress_approved: data.approved,
              }
            : null
        );
        // Reload to get the movies
        if (data.status === 'completed') {
          loadPlaylist();
        }
      } else {
        setPlaylist((prev) =>
          prev
            ? {
                ...prev,
                progress_found: data.found,
                progress_approved: data.approved,
              }
            : null
        );
      }
    });

    return () => {
      controller.abort();
    };
  }, [playlistId, playlistStatus, loadPlaylist]);

  const handleDelete = async () => {
    if (!playlistId || !playlist) return;

    if (!window.confirm(`Are you sure you want to delete "${playlist.name}"?`)) {
      return;
    }

    setDeleting(true);
    try {
      await deletePlaylist(playlistId);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete playlist');
      setDeleting(false);
    }
  };

  const handleRemoveMovie = async (movie: PlaylistMovie) => {
    if (!playlistId) return;

    if (!window.confirm(`Remove "${movie.title}" from this playlist?`)) {
      return;
    }

    setRemovingMovieId(movie.tmdb_id);
    try {
      await removeMovieFromPlaylist(playlistId, movie.tmdb_id);
      setMovies((prev) => prev.filter((m) => m.tmdb_id !== movie.tmdb_id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove movie');
    } finally {
      setRemovingMovieId(null);
    }
  };

  const handleMovieAdded = (_movie: PlaylistMovie) => {
    // Reload the current page to reflect the new movie and updated pagination
    loadPlaylist(pagination.page);
  };

  const handleToggleWatched = async (movie: PlaylistMovie) => {
    const tmdbId = movie.tmdb_id;
    const isCurrentlyWatched = watchedMovies.has(tmdbId);

    // Optimistic update
    setTogglingWatched(tmdbId);
    setWatchedMovies((prev) => {
      const next = new Set(prev);
      if (isCurrentlyWatched) {
        next.delete(tmdbId);
      } else {
        next.add(tmdbId);
      }
      return next;
    });

    // For Want to Watch: optimistically remove the movie when marking as watched
    if (isWantToWatch && !isCurrentlyWatched) {
      setMovies((prev) => prev.filter((m) => m.tmdb_id !== tmdbId));
    }

    try {
      if (isCurrentlyWatched) {
        await unmarkMovieWatched(tmdbId);
      } else {
        await markMovieWatched(tmdbId);
      }
    } catch (err) {
      // Revert on error
      setWatchedMovies((prev) => {
        const next = new Set(prev);
        if (isCurrentlyWatched) {
          next.add(tmdbId);
        } else {
          next.delete(tmdbId);
        }
        return next;
      });
      // Revert movie removal for Want to Watch
      if (isWantToWatch && !isCurrentlyWatched) {
        loadPlaylist(pagination.page);
      }
      setError(err instanceof Error ? err.message : 'Failed to update watched status');
    } finally {
      setTogglingWatched(null);
    }
  };

  const handleHideWatchedChange = (checked: boolean) => {
    setHideWatched(checked);
    if (playlistId) {
      localStorage.setItem(`playlist-${playlistId}-hide-watched`, String(checked));
    }
  };

  const handlePlaylistUpdated = (updatedPlaylist: Playlist) => {
    setPlaylist(updatedPlaylist);
    // If the playlist is now processing, reload will happen via SSE
    // If it's still completed (only name changed), we're good
  };

  if (loading) {
    return <div className="empty">Loading playlist...</div>;
  }

  if (error || !playlist) {
    return (
      <div className="card">
        <div className="empty">
          <p>{error || 'Playlist not found'}</p>
          <Link to="/" className="link-button">
            Back to Playlists
          </Link>
        </div>
      </div>
    );
  }

  const isProcessing = playlist.status === 'pending' || playlist.status === 'processing';

  return (
    <div className="playlist-detail">
      <div className="playlist-detail-header">
        <Link to="/" className="link-button">
          ← Back to Playlists
        </Link>
        <div className="playlist-header-actions">
          {isOwner && !isSystemPlaylist && (
            <button className="secondary" onClick={() => setShowShareModal(true)}>
              Share
            </button>
          )}
          {isOwner && !isSystemPlaylist && (
            <OverflowMenu
              onEdit={() => setShowEditModal(true)}
              onDelete={handleDelete}
              deleting={deleting}
            />
          )}
        </div>
      </div>

      {sharedBy && (
        <div className="shared-by-banner">
          Shared by <strong>@{sharedBy.username || sharedBy.name || 'Unknown'}</strong>
          {permission === 'read' && <span className="permission-badge">View only</span>}
          {permission === 'edit' && <span className="permission-badge edit">Can edit</span>}
        </div>
      )}

      <div className="card playlist-info">
        <div className="playlist-info-header">
          <h2>{playlist.name}</h2>
          {playlist.status !== 'completed' && (
            <PlaylistStatusBadge status={playlist.status} errorMessage={playlist.error_message} />
          )}
        </div>
        {playlist.prompt && <p className="playlist-prompt-detail">"{playlist.prompt}"</p>}
        {!playlist.prompt && !isSystemPlaylist && (
          <p className="playlist-prompt-detail" style={{ fontStyle: 'italic', opacity: 0.6 }}>
            Manual playlist - add movies using the search feature
          </p>
        )}
        {isWantToWatch && (
          <p className="playlist-prompt-detail" style={{ fontStyle: 'italic', opacity: 0.6 }}>
            Movies you want to watch. They move to Watched when you mark them.
          </p>
        )}
        {isWatchedPlaylist && (
          <p className="playlist-prompt-detail" style={{ fontStyle: 'italic', opacity: 0.6 }}>
            All movies you've marked as watched.
          </p>
        )}

        {isProcessing && progress && (
          <div className="processing-status">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width:
                    progress.found > 0
                      ? `${Math.min(100, (progress.approved / Math.max(progress.found, 1)) * 100)}%`
                      : '0%',
                }}
              />
            </div>
            <p className="progress-message">{progress.message}</p>
          </div>
        )}

        {playlist.status === 'failed' && playlist.error_message && (
          <div className="error-message">{playlist.error_message}</div>
        )}
      </div>

      {movies.length > 0 && (
        <div className="card" style={{ marginTop: '16px' }}>
          {/* Watched Progress (hidden for Watched playlist since everything is watched) */}
          {!isWatchedPlaylist && (
            <div className="watched-progress-section">
              <div className="watched-progress-header">
                <div className="watched-progress-stats">
                  <span className="watched-progress-text">
                    {totalWatchedCount}/{totalMovieCount} watched ({watchedPercentage}%)
                  </span>
                  {friendsWatchedCount > 0 && (
                    <span className="friends-watched-count">
                      {friendsWatchedCount} watched by friends
                    </span>
                  )}
                </div>
                <label className="hide-watched-toggle">
                  <input
                    type="checkbox"
                    checked={hideWatched}
                    onChange={(e) => handleHideWatchedChange(e.target.checked)}
                  />
                  <span>Hide watched</span>
                </label>
              </div>
              <div className="watched-progress-bar">
                <div className="watched-progress-fill" style={{ width: `${watchedPercentage}%` }} />
              </div>
            </div>
          )}

          <div className="playlist-movies-header">
            <h3>
              {totalMovieCount} Movies
              {hideWatched && movies.length - filteredMovies.length > 0
                ? ` (${movies.length - filteredMovies.length} hidden on page)`
                : ''}
            </h3>
            <div className="playlist-movies-actions">
              {canEdit && !isWatchedPlaylist && (
                <button onClick={() => setShowAddModal(true)}>Add Movie</button>
              )}
            </div>
          </div>

          {/* Sort and Filter Controls */}
          <div className="playlist-filters">
            {/* Mobile filter toggle */}
            <button className="filter-toggle-mobile" onClick={() => setShowFilters(!showFilters)}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                width="16"
                height="16"
              >
                <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
              </svg>
              {showFilters ? 'Hide Filters' : 'Filters'}
              {(languageFilter || yearMin || yearMax || genreFilter) && (
                <span className="filter-active-badge" />
              )}
            </button>

            <div className={`filter-row ${showFilters ? 'filter-row-visible' : ''}`}>
              <select
                value={sortBy}
                onChange={(e) =>
                  setSortBy(e.target.value as 'year' | 'title' | 'watched_at' | 'added_at')
                }
                className="filter-select"
              >
                {isWatchedPlaylist && (
                  <option value="watched_at">Sort by Watched Date</option>
                )}
                {isWantToWatch && <option value="added_at">Sort by Date Added</option>}
                <option value="year">Sort by Year</option>
                <option value="title">Sort by Title</option>
              </select>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                className="filter-select"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
              {filterOptions.languages.length > 0 && (
                <select
                  value={languageFilter}
                  onChange={(e) => setLanguageFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Languages</option>
                  {filterOptions.languages.map((lang) => (
                    <option key={lang} value={lang}>
                      {lang.toUpperCase()}
                    </option>
                  ))}
                </select>
              )}
              {filterOptions.genres.length > 0 && (
                <select
                  value={genreFilter}
                  onChange={(e) => setGenreFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Genres</option>
                  {filterOptions.genres.map((genre) => (
                    <option key={genre} value={genre}>
                      {genre}
                    </option>
                  ))}
                </select>
              )}
              {filterOptions.yearRange && (
                <div className="year-range-filter">
                  <input
                    type="number"
                    placeholder={filterOptions.yearRange.min.toString()}
                    value={yearMin ?? ''}
                    onChange={(e) =>
                      setYearMin(e.target.value ? parseInt(e.target.value, 10) : undefined)
                    }
                    className="year-input"
                    min={filterOptions.yearRange.min}
                    max={filterOptions.yearRange.max}
                  />
                  <span className="year-separator">-</span>
                  <input
                    type="number"
                    placeholder={filterOptions.yearRange.max.toString()}
                    value={yearMax ?? ''}
                    onChange={(e) =>
                      setYearMax(e.target.value ? parseInt(e.target.value, 10) : undefined)
                    }
                    className="year-input"
                    min={filterOptions.yearRange.min}
                    max={filterOptions.yearRange.max}
                  />
                </div>
              )}
              <input
                type="text"
                placeholder="Search movies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
          <div className="movie-list">
            {filteredMovies.map((movie) => {
              const isWatched = watchedMovies.has(movie.tmdb_id);
              const isFriendWatched = friendsWatchedMovies.has(movie.tmdb_id);
              return (
                <div
                  key={movie.tmdb_id}
                  className={`movie-item ${isWatched ? 'movie-item-watched' : ''}`}
                >
                  {movie.poster_url ? (
                    <img src={movie.poster_url} alt={movie.title || ''} />
                  ) : (
                    <div className="no-image movie-poster-placeholder">No image</div>
                  )}
                  <div className="movie-item-content">
                    <div className="movie-item-info">
                      <h4>
                        <Link to={`/movies/${movie.tmdb_id}`} className="movie-title-link">
                          {movie.original_title || movie.title}
                        </Link>
                        {isFriendWatched && (
                          <span className="friend-watched-badge" title="Watched by friends">
                            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-6 8c0-2.67 5.33-4 6-4s6 1.33 6 4v1H6v-1zm12-8c1.1 0 2-.9 2-2s-.9-2-2-2c-.36 0-.69.1-1 .27.64.82 1 1.86 1 2.73 0 .87-.36 1.91-1 2.73.31.17.64.27 1 .27zm3 8c0-.78-.29-1.53-.79-2.17C18.56 16.62 16.09 16 14 16c-.22 0-.44.01-.66.02 1.1.67 1.66 1.47 1.66 2.48v1.5h5v-1z" />
                            </svg>
                          </span>
                        )}
                      </h4>
                      {movie.overview && <TruncatedText text={movie.overview} maxLines={2} />}
                      <div className="movie-meta">
                        {movie.year && <span>{movie.year}</span>}
                        {movie.runtime && <span>{movie.runtime} min</span>}
                        {movie.original_language && (
                          <span className="language-badge">
                            {movie.original_language.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <RatingBadges
                        imdbRating={movie.imdb_rating}
                        rottenTomatoesRating={movie.rotten_tomatoes_rating}
                        metacriticRating={movie.metacritic_rating}
                      />
                    </div>
                    <div className="movie-item-actions">
                      <button
                        className={`action-button watched-toggle-button ${isWatched ? 'watched' : ''}`}
                        onClick={() => handleToggleWatched(movie)}
                        disabled={togglingWatched === movie.tmdb_id}
                        aria-label={isWatched ? 'Mark as unwatched' : 'Mark as watched'}
                      >
                        {togglingWatched === movie.tmdb_id ? (
                          <span className="action-spinner" />
                        ) : isWatched ? (
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                        ) : (
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                        <span className="action-label">Watched</span>
                      </button>
                      {canEdit && !isWatchedPlaylist && (
                        <button
                          className="action-button remove-movie-button"
                          onClick={() => handleRemoveMovie(movie)}
                          disabled={removingMovieId === movie.tmdb_id}
                          aria-label="Remove from playlist"
                        >
                          {removingMovieId === movie.tmdb_id ? (
                            <span className="action-spinner" />
                          ) : (
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14" />
                              <path d="M10 11v6M14 11v6" />
                            </svg>
                          )}
                          <span className="action-label">Remove</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="pagination-button"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                className="pagination-button"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {playlist.status === 'completed' && movies.length === 0 && (
        <div className="card" style={{ marginTop: '16px' }}>
          <div className="empty">
            {isWantToWatch ? (
              <p>
                Your Want to Watch list is empty. Add movies from the Explore page or any playlist.
              </p>
            ) : isWatchedPlaylist ? (
              <p>You haven't watched any movies yet. Mark movies as watched to see them here.</p>
            ) : (
              <>
                <p>No movies matched your criteria. Try a different prompt.</p>
                {canEdit && (
                  <button onClick={() => setShowAddModal(true)} style={{ marginTop: '12px' }}>
                    Add Movie Manually
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {showAddModal && playlistId && (
        <AddMovieModal
          playlistId={playlistId}
          onClose={() => setShowAddModal(false)}
          onMovieAdded={handleMovieAdded}
        />
      )}

      {showEditModal && playlist && (
        <EditPlaylistModal
          playlist={playlist}
          movieCount={movies.length}
          onClose={() => setShowEditModal(false)}
          onUpdated={handlePlaylistUpdated}
        />
      )}

      {showShareModal && playlistId && (
        <SharePlaylistModal
          isOpen={showShareModal}
          playlistId={playlistId}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
}
